import React, { useEffect, useState } from "react";
import "./BookingStyles.scss";
import LogoContainer from "../../components/logocontainer/LogoContainer";
import Button from "../../components/button/Button";
import BaseHeader from "../../components/baseheader/BaseHeader";
import TimeSlot from "../../components/timeslot/TimeSlot";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../application/store";
import { useParams, useSearchParams } from "react-router-dom";
import {
  setAvailableDaySpots,
  setAvailableSpots,
  setDate,
  setGuest,
  setLocationId,
  setOrganizationalDetails,
  setPrePaymentData,
  setShiftDetails,
  setTableDuration,
  setTime,
} from "../../../application/reducer/bookingSlice";
import {
  convertTime,
  findShiftByTime,
  generateUniqueDates,
  getAllTimeSlotsForDate,
  getReservationCovers,
  getTimeSlotsForDate,
  getTimeSlotsWithShifts,
} from "../../../application/utils";
import {
  getBlockedReservations,
  getOrganizationDetails,
  getPrePaymentDetails,
  getShiftDetails,
  getSpotAvailability,
} from "../../../infra/apis/bookings/requests/Booking";
import moment from "moment";
import Alert from "../auth/reservation/tableDetails/Alert";
import DatePicker2 from "../../components/ReservationComponents/datePicker/DatePicker2";
import CustomBanner from "../../components/customBanner/CustomBanner";

const BookingPageOne: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSlotsLoading, setIsSlotsLoading] = useState(false);
  const [blockedData, setBlockedData] = useState<any>([]);

  const dispatch = useDispatch();
  const guest = useSelector((state: RootState) => state.booking.guest);
  const date = useSelector((state: RootState) => state.booking.date);
  const time = useSelector((state: RootState) => state.booking.time);

  const shiftData = useSelector(
    (state: RootState) => state.booking.shiftDetails,
  );
  const availableSpots = useSelector(
    (state: RootState) => state.booking.availableSpots,
  );
  const availableDaySpots = useSelector(
    (state: RootState) => state.booking.availableDaySpots,
  );
  const timeSlots = getTimeSlotsForDate(
    date,
    guest === 0 ? getReservationCovers(date, shiftData)[0] : guest,
    shiftData,
  );
  const timeSlotsWithShift = getTimeSlotsWithShifts(
    date,
    guest === 0 ? getReservationCovers(date, shiftData)[0] : guest,
    shiftData,
  );
  const timeDaySlots = Array.from(new Set(timeSlots));
  const timeDaySlotsWithShifts = Array.from(new Set(timeSlotsWithShift));
  const allDaySlot = getAllTimeSlotsForDate(date, shiftData).sort();
  const allDaySlots = Array.from(new Set(allDaySlot));
  const organizationDetails = useSelector(
    (state: RootState) => state.booking.organizationDetails,
  );
  const { locationId } = useParams<{
    locationId: string;
  }>();
  const [searchParams] = useSearchParams();
  const bookingDate = searchParams.get("Date");
  const openingHours = organizationDetails?.OpeningHours?.filter(
    (day: any) => !day.Closed,
  ).map((day: any) => day.Day);

  const getShiftsByDayWithTimeSlots = () => {
    const dayUpper = moment(date).format("dddd").toUpperCase();
    let shifts = shiftData.filter((shift: any) => shift.TimeWindow);

    const availableShifts = shifts
      .filter((shift: any) => shift.IsActive && shift.Type === "RECURRING")
      .filter((shift: any) =>
        shift.ShiftActiveDay.some(
          (activeDay: any) => activeDay.Day === dayUpper,
        ),
      );
    if (availableShifts.length === 0) return null;

    const timeSlots = availableShifts.flatMap((shift: any) => {
      const { Timeslot } = shift.ShiftAvailability;
      return shift.TimeWindow.flatMap((timeWindow: any) => {
        const { StartTime, EndTime, Percentage } = timeWindow;

        const startDateTime = new Date(`1970-01-01T${StartTime}:00Z`);
        const endDateTime = new Date(`1970-01-01T${EndTime}:00Z`);

        const slots = [];
        let currentTime = startDateTime;
        while (currentTime < endDateTime) {
          const slotEndTime = new Date(
            currentTime.getTime() + Timeslot * 60000,
          );
          slots.push({
            Time: currentTime.toISOString().substr(11, 5),
            Percentage: Percentage,
            Busy: timeWindow.Configuration.Busy,
          });

          currentTime = slotEndTime;
        }

        return slots;
      });
    });

    return timeSlots;
  };
  const navigate = useNavigate();
  const getCurrentSlot = async (availableSpotsDetails: any) => {
    return timeDaySlots
      .map((option) =>
        availableSpotsDetails.find(
          (slot: any) => convertTime(slot.StartTime) === option,
        ),
      )
      .find((slot) => slot !== undefined);
  };

  const findFirstBeforeSlot = async (
    availableSpotsDetails: any,
    blocked: any[],
  ) => {
    const slot = await getCurrentSlot(availableSpotsDetails);
    let timeData = "";
    if (slot) {
      const timeValue = convertTime(slot.StartTime);

      let window =
        getShiftsByDayWithTimeSlots() &&
        getShiftsByDayWithTimeSlots().find(
          (wind: any) => wind.Time === timeValue,
        );

      const startTime = new Date(slot.StartTime);
      const currentTime = new Date();

      const currentTimeUTC = new Date(
        startTime.getUTCFullYear(),
        startTime.getUTCMonth(),
        startTime.getUTCDate(),
        startTime.getUTCHours(),
        startTime.getUTCMinutes(),
        startTime.getUTCSeconds(),
      );
      let windowAvailable = "AVAILABLE";
      if (window !== undefined) {
        if (window?.Busy) {
          if (slot?.ProjectedOccupancy * 100 >= window?.Percentage) {
            windowAvailable = "BUSY";
          }
        } else {
          if (slot?.ProjectedOccupancy * 100 >= window?.Percentage) {
            windowAvailable = "UNAVAILABLE";
          }
        }
      }
      const isBefore = currentTimeUTC < currentTime;
      if (slot.Available) {
        if (
          !isBefore &&
          !blocked.includes(timeValue) &&
          windowAvailable !== "UNAVAILABLE"
        ) {
          timeData = timeValue;
        }
      }
    }
    return timeData;
  };

  const handleTimeSlots = async (availableTimeSlots: any, blocked: any[]) => {
    if (!isDayAvailable()) {
      dispatch(setTime(""));
    } else {
      if (availableTimeSlots.length > 0) {
        const firstSlotBefore = await findFirstBeforeSlot(
          availableTimeSlots,
          blocked,
        );
        if (firstSlotBefore !== "") {
          dispatch(setTime(firstSlotBefore));
        } else {
          dispatch(setTime(""));
        }
      } else {
        dispatch(setTime(""));
      }
    }
  };

  useEffect(() => {
    localStorage.setItem("wrlId", locationId || "");
    dispatch(setLocationId(locationId || ""));
    getUpdateData();
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      setIsSlotsLoading(true);
      let blocked: any[] = [];
      getBlockedReservations(
        locationId || "",
        moment(date).format("YYYY-MM-DD"),
      ).then((res) => {
        if (res) {
          blocked = getOnlineTimeSlots(res);
          setBlockedData(getOnlineTimeSlots(res));
        } else {
          setBlockedData([]);
        }
      });
      getSpotAvailability(locationId || "", moment(date).format("YYYY-MM-DD"))
        .then((response: any) => {
          if (!getReservationCovers(date, shiftData).includes(guest)) {
            dispatch(setGuest(getReservationCovers(date, shiftData)[0]));
          }
          dispatch(setAvailableDaySpots(response));
          const availableTimeSlots = response.filter(
            (spot: any) => spot.PartySize === guest && spot.SpotsOpen > 0,
          );
          dispatch(setAvailableSpots(availableTimeSlots));
          handleTimeSlots(availableTimeSlots, blocked);
          setIsLoading(false);
          setIsSlotsLoading(false);
        })
        .catch(() => {
          setIsLoading(true);
          setIsSlotsLoading(true);
        });
    };
    fetchData();
  }, [date]);
  const getOnlineTimeSlots = (blocks: any) => {
    const slots: any[] = [];
    blocks.forEach((block: any) => {
      if (block.Configuration.Online) {
        const startTime = block.BlockTime.Start;
        const endTime = block.BlockTime.End;
        let start = new Date(`1970-01-01T${startTime}:00Z`);
        let end = new Date(`1970-01-01T${endTime}:00Z`);
        while (start < end) {
          const slotStart = start.toISOString().substring(11, 16);
          start.setMinutes(start.getMinutes() + 15);
          if (start <= end) {
            slots.push(slotStart);
          }
        }
      }
    });

    return slots.sort();
  };

  useEffect(() => {
    const availableTimeSlots = availableDaySpots?.filter(
      (spot: any) => spot.PartySize === guest && spot.SpotsOpen > 0,
    );
    dispatch(setAvailableSpots(availableTimeSlots));
    handleTimeSlots(availableTimeSlots, blockedData);
  }, [guest]);
  const getUpdateData = async () => {
    await getOrganizationDetails(locationId || "").then(
      (orgnizationDetails) => {
        dispatch(setOrganizationalDetails(orgnizationDetails));
      },
    );
    await getShiftDetails(locationId || "").then((shiftData) => {
      dispatch(setShiftDetails(shiftData));
      dispatch(setGuest(getReservationCovers(date, shiftData)[0]));
      const filteredDates = getDateOptions();
      if (bookingDate) {
        dispatch(setDate(bookingDate));
      } else {
        dispatch(setDate(filteredDates[0]?.value));
      }
    });
  };
  const getDateOptions = () => {
    const options = [];
    const today = new Date();
    for (let i = 0; i <= 29; i++) {
      const date = new Date();
      date.setDate(today.getDate() + i);

      let label = "";
      if (i === 0) {
        label = "Today";
      } else if (i === 1) {
        label = "Tomorrow";
      } else {
        label = date.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        });
      }

      options.push({ value: date.toISOString().slice(0, 10), label });
    }
    return options;
  };
  const handleGuestChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setGuest(parseInt(e.target.value, 10)));
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    // localStorage.setItem("bDate", e.target.value);
    dispatch(setDate(e.target.value));
  };
  const handleTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    let slot = availableSpots.find(
      (slot: any) => convertTime(slot.StartTime) === e.target.value,
    );
    let duration = slot.Duration / 60;

    dispatch(setTableDuration(duration));
    dispatch(setTime(e.target.value));
  };
  const handleClick = () => {
    if (guest > 0 && date !== "" && time !== "") {
      navigate("/booking/customer-details");
    }
  };
  const isShiftAvailable = (day: any) => {
    const onetime = shiftData
      .filter((shift: any) => shift.IsActive && shift.Type === "ONETIME")
      .flatMap((shift: any) => shift.TimeSchedule);
    let shiftsDays = shiftData
      .filter((shift: any) => shift.IsActive && shift.Type === "RECURRING")
      ?.flatMap((shift: any) => shift.ShiftActiveDay)
      .map((day: any) => day.Day.toLowerCase())
      .filter(
        (day: any, index: number, self: any) => index === self.indexOf(day),
      );
    let selectedDay = moment(day).format("dddd").toLowerCase();
    return (
      shiftsDays.includes(selectedDay) ||
      generateUniqueDates(onetime).includes(moment(date).format("M/D/YYYY"))
    );
  };
  const isSlotsAvailble = () => {
    return allDaySlots
      .map((option: any, index) => {
        let slot = availableSpots.find(
          (slot: any) => convertTime(slot.StartTime) === option,
        );
        let isAvailable = false;
        let isBefore = false;
        if (slot !== undefined) {
          isAvailable = slot ? slot.Available : false;
          let startTime = new Date(slot?.StartTime);
          let currentTime = new Date();
          let currentTimeUTC = new Date(
            startTime.getUTCFullYear(),
            startTime.getUTCMonth(),
            startTime.getUTCDate(),
            startTime.getUTCHours(),
            startTime.getUTCMinutes(),
            startTime.getUTCSeconds(),
          );
          isBefore = currentTimeUTC < currentTime;
        }
        return (
          !isAvailable ||
          isBefore ||
          !timeDaySlots.includes(option) ||
          blockedData.includes(option)
        );
      })
      .some((element) => element !== true);
  };
  const isDayAvailable = () => {
    const onetime = shiftData
      .filter((shift: any) => shift.IsActive && shift.Type === "ONETIME")
      .flatMap((shift: any) => shift.TimeSchedule);
    return (
      openingHours?.includes(moment(date).format("dddd")) ||
      generateUniqueDates(onetime).includes(moment(date).format("M/D/YYYY"))
    );
  };
  useEffect(() => {
    const fetchPaymentDetails = async () => {
      let selectedShift: any =
        findShiftByTime(timeDaySlotsWithShifts, time) || null;
      console.log(selectedShift, "selectedShift");
      if (selectedShift) {
        localStorage.setItem("bss_oid", selectedShift.shiftId);
        try {
          const shiftDet: any = await getPaymentDetails(selectedShift.shiftId);
          if (shiftDet.length > 0) {
            let paymentData = shiftDet.filter((shift: any) =>
              shift.Shift.filter(
                (value: any) =>
                  value.IsEnable && value.Id === selectedShift.shiftId,
              ),
            )[0];
            if (paymentData) {
              dispatch(setPrePaymentData(paymentData));
            } else {
              dispatch(setPrePaymentData(null));
            }
          }
        } catch (error) {
          console.error("Error fetching payment details:", error);
        }
      } else {
      }
    };

    if (isDayAvailable() && isShiftAvailable(date)) {
      fetchPaymentDetails();
    } else {
      dispatch(setPrePaymentData(null));
    }
  }, [time, date]);

  const getPaymentDetails = async (id: string) => {
    try {
      const res = await getPrePaymentDetails(id, date);
      return res || [];
    } catch (error) {
      return [];
    }
  };
  console.log(date, "date");
  return (
    <div className='page'>
      <div className='border' />
      {isLoading ? (
        <div className='loader' />
      ) : (
        <div className='BookingPageOne'>
          <div className='left-container'>
            {/* <div className='logo-witmeg'>
              <img alt='' src={witmeg} />
            </div> */}
            <LogoContainer />
          </div>
          <div className='right-container'>
            <div className='form-container'>
              <BaseHeader />
              <CustomBanner />
              <div className='guest-and-date-selector'>
                {isDayAvailable() && isShiftAvailable(date) && (
                  <div className='left'>
                    <div className='dropdown'>
                      <label htmlFor='guests'>
                        Party Size
                        <span style={{ color: "red", marginLeft: "0.2rem" }}>
                          *
                        </span>
                      </label>
                      <select
                        id='guests'
                        value={guest}
                        onChange={handleGuestChange}
                      >
                        {getReservationCovers(date, shiftData).map(
                          (value, index) => (
                            <option key={index} value={value}>
                              {value}
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                  </div>
                )}

                <div className='right'>
                  <div className='dropdown'>
                    <label htmlFor='dates'>
                      Date
                      <span style={{ color: "red", marginLeft: "0.2rem" }}>
                        *
                      </span>
                    </label>
                    {organizationDetails?.GeneralSettings?.BookingWidget?.Days <
                    30 ? (
                      <select
                        id='dates'
                        value={date}
                        onChange={handleDateChange}
                      >
                        {getDateOptions().map((option) => (
                          <option
                            key={option.value}
                            value={option.value}
                            disabled={
                              !isDayAvailable || !isShiftAvailable(option.value)
                            }
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <DatePicker2
                        selectedDate={date}
                        setSelectedDate={(value) => {
                          dispatch(setDate(moment(value).format("yyyy-MM-DD")));
                        }}
                        maxDays={
                          organizationDetails?.GeneralSettings?.BookingWidget
                            ?.Days
                        }
                      />
                    )}
                  </div>
                  <div className='dropdown'>
                    {isDayAvailable() &&
                      !isSlotsLoading &&
                      isShiftAvailable(date) && (
                        <>
                          <label htmlFor='dates'>
                            Time
                            <span
                              style={{ color: "red", marginLeft: "0.2rem" }}
                            >
                              *
                            </span>
                          </label>
                          <select
                            id='dates'
                            value={time}
                            onChange={handleTimeChange}
                            className={
                              !isSlotsAvailble() ? "diabled-select" : ""
                            }
                          >
                            {allDaySlots.map((option: any, index) => {
                              let slot = availableSpots.find(
                                (slot: any) =>
                                  convertTime(slot.StartTime) === option,
                              );
                              let window =
                                getShiftsByDayWithTimeSlots() &&
                                getShiftsByDayWithTimeSlots().find(
                                  (wind: any) => wind.Time === option,
                                );

                              let isAvailable = false;
                              let isBefore = false;
                              if (slot !== undefined) {
                                isAvailable = slot ? slot.Available : false;
                                let startTime = new Date(slot?.StartTime);
                                let currentTime = new Date();
                                let currentTimeUTC = new Date(
                                  startTime.getUTCFullYear(),
                                  startTime.getUTCMonth(),
                                  startTime.getUTCDate(),
                                  startTime.getUTCHours(),
                                  startTime.getUTCMinutes(),
                                  startTime.getUTCSeconds(),
                                );
                                isBefore = currentTimeUTC < currentTime;
                              }
                              let windowAvailable = "AVAILABLE";
                              if (window !== undefined) {
                                if (window?.Busy) {
                                  if (
                                    slot?.ProjectedOccupancy * 100 >=
                                    window?.Percentage
                                  ) {
                                    windowAvailable = "BUSY";
                                  }
                                } else {
                                  if (
                                    slot?.ProjectedOccupancy * 100 >=
                                    window?.Percentage
                                  ) {
                                    windowAvailable = "UNAVAILABLE";
                                  }
                                }
                              }
                              const isDisabled =
                                !isAvailable ||
                                isBefore ||
                                !timeDaySlots.includes(option) ||
                                windowAvailable === "UNAVAILABLE" ||
                                blockedData.includes(option);
                              return (
                                <option
                                  key={index}
                                  value={time === "" ? "" : option}
                                  disabled={isDisabled}
                                  style={{
                                    backgroundColor: isDisabled
                                      ? "#ededed"
                                      : "",
                                  }}
                                >
                                  {option}
                                </option>
                              );
                            })}
                          </select>
                        </>
                      )}
                  </div>
                </div>
              </div>

              {isDayAvailable() ? (
                isShiftAvailable(date) ? (
                  isSlotsLoading ? (
                    <div style={{ marginBottom: "15%", marginTop: "-5%" }}>
                      <div className='loader' />
                    </div>
                  ) : (
                    <>
                      <>
                        <div className='time-label'>
                          <label htmlFor='time'>
                            Select a time
                            <span
                              style={{ color: "red", marginLeft: "0.2rem" }}
                            >
                              *
                            </span>
                          </label>
                        </div>

                        <TimeSlot
                          timeSlots={timeDaySlots}
                          allDaySlots={allDaySlots}
                          timeWindow={getShiftsByDayWithTimeSlots() ?? []}
                          blockedTimes={blockedData}
                        />
                      </>
                      {!isSlotsAvailble() ? (
                        <Alert
                          type='warning'
                          content='Slots are unavailable for the reservation'
                        />
                      ) : (
                        <div className='time-window-des'>
                          <div className='des'>
                            <div className='box'>
                              <div className='unavailable' />
                            </div>
                            Unavailable
                          </div>
                          <div className='des'>
                            <div className='box'>
                              <div className='busy' />
                            </div>
                            Busy
                          </div>
                          <div className='des'>
                            <div className='box'>
                              <div className='available' />
                            </div>
                            Available
                          </div>
                        </div>
                      )}
                    </>
                  )
                ) : (
                  <div className='closed'>
                    <div>
                      <div className='header'>Unavailable</div>
                      <div className='sub'>
                        Shifts are unavailable for the day
                      </div>
                    </div>
                  </div>
                )
              ) : (
                <div className='closed'>
                  <div>
                    <div className='header'>Closed</div>
                    <div className='sub'>Restaurant is closed for the day</div>
                  </div>
                </div>
              )}

              <div className='button-container'>
                <Button
                  text='continue'
                  onClick={handleClick}
                  disable={time === "" ? true : false}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default BookingPageOne;
